// Default texts of the search dropdown, per language (iteration 26). defineDropdown (../define.ts)
// fills every key below the config leaves `undefined` from the pack of the config's `locale`
// (./locale.ts textLanguage) — so this module ships only in bundles whose config defines a
// dropdown — and its `chatbot.localization` per key with the chat-text merge its `chatbotUi`
// carries (the facade's `dropdownChat`; ./chatbot.ts), so the chat's texts ship only with the
// chat. Pure data + a pure merge.
//
// Sources (the most common value per key and language across customers/*/config.ts, written in
// sentence case — some shops write e.g. "ZOBRAZIT VŠECHNY VÝSLEDKY" as styling):
//   - cs, sk, en: from the customers' configs; en `welcomeMessage` / `recommendedProductsTitle`
//     as the Freshlabels English shops write them. `inputPlaceholder` / `noResultsFoundMessage`:
//     ./shared.ts (sources there).
//   - de: `recommendedProductsTitle`, `foundProductsTitle`, `showAllMessage` from freshlabels-de;
//     `welcomeMessage` drafted in the formal "Sie" register (freshlabels-de uses "du").
//
// Text keys of the dropdown config deliberately WITHOUT a default (the config sets them itself):
//   - `searchingForMessage` / `wouldYouLikeToSearchForMessage`: setting them switches on the
//     "searching for X, would you like to search for Y?" hint; a default would turn it on for
//     every config that leaves them out today (the Freshlabels shops, uhabakuka-cz).
//   - `foundProductsTitleShort` (the products tab label of the narrow layout): optional, sdk-search
//     falls back to `foundProductsTitle` (the config's own or the pack's) when it is unset.
//   - `recommendedPhrasesTitle`: setting it switches on the phrases list (with `demoQueries`).
//   - `inStockText`, `defaultButtonLabel`: per-shop labels (a stock label; the card button's
//     label, whose absence means no button), not UI language texts.
//   - `content[].title`: per-group titles of the shop's own content types.

import type { DropdownLocalizationConfig } from "@raventic/sdk-search-v1";
import { CHAT_TEXTS_PROPERTY } from "./chatbot.ts";
import type { ChatTextsFiller } from "./chatbot.ts";
import { fillTexts, textLanguage } from "./locale.ts";
import type { TextLanguage, TextPacks } from "./locale.ts";
import { INPUT_PLACEHOLDERS, NO_RESULTS_MESSAGES } from "./shared.ts";

export type DropdownTextKey =
  | "inputPlaceholder"
  | "welcomeMessage"
  | "recommendedProductsTitle"
  | "foundProductsTitle"
  | "noResultsFoundMessage"
  | "showAllMessage";
export type DropdownTexts = Required<Pick<DropdownLocalizationConfig, DropdownTextKey>>;

// The dropdown-only texts; the shared ones (search box placeholder, no-results message) come
// from ./shared.ts. Plain literals: the pack module stays tree-shakable (no module-level reads).
const DROPDOWN_OWN_TEXTS = {
  cs: {
    welcomeMessage: "Najděte přesně to, co hledáte!",
    recommendedProductsTitle: "Oblíbené produkty",
    foundProductsTitle: "Produkty",
    showAllMessage: "Zobrazit všechny výsledky",
  },
  sk: {
    welcomeMessage: "Nájdite presne to, čo hľadáte!",
    recommendedProductsTitle: "Obľúbené produkty",
    foundProductsTitle: "Produkty",
    showAllMessage: "Zobraziť všetky výsledky",
  },
  en: {
    welcomeMessage: "What are you looking for?",
    recommendedProductsTitle: "Recommended",
    foundProductsTitle: "Products",
    showAllMessage: "Show all results",
  },
  de: {
    welcomeMessage: "Wonach suchen Sie?",
    recommendedProductsTitle: "Wir empfehlen",
    foundProductsTitle: "Produkte",
    showAllMessage: "Alle Ergebnisse anzeigen",
  },
} as const satisfies TextPacks<Omit<DropdownTexts, "inputPlaceholder" | "noResultsFoundMessage">>;

// The dropdown's whole pack for `language`. Built per call, not as a module-level constant: a
// bundler keeps a module-level spread / property read of another module's object as a possible
// side effect, which would ship every pack in every bundle.
export function dropdownTexts(language: TextLanguage): DropdownTexts {
  return {
    inputPlaceholder: INPUT_PLACEHOLDERS[language],
    noResultsFoundMessage: NO_RESULTS_MESSAGES[language],
    ...DROPDOWN_OWN_TEXTS[language],
  };
}

// A dropdown config with its missing texts filled (see the header): its own, and — when its
// `chatbotUi` carries the chat-text merge — its `chatbot.localization`. Never mutates; a config
// that already sets every text (and every chat text) is returned as-is. An enabled chat whose
// `chatbotUi` lacks the merge (sdk-assistant's own `dropdownChat`) is warned about: its texts stay
// the SDK's English defaults.
export function withDropdownTexts<C extends object>(config: C): C {
  const { locale, chatbot, chatbotUi } = config as { locale?: unknown; chatbot?: unknown; chatbotUi?: unknown };
  const language = textLanguage(locale);
  const filled = fillTexts(config, dropdownTexts(language));
  const fillChat =
    typeof chatbotUi === "object" && chatbotUi !== null
      ? (chatbotUi as { [CHAT_TEXTS_PROPERTY]?: unknown })[CHAT_TEXTS_PROPERTY]
      : undefined;
  if (chatbot === undefined) return filled;
  if (typeof fillChat !== "function") {
    if (fillChat === undefined && chatbotUi && (chatbot as { enabled?: unknown } | null)?.enabled === true) {
      console.warn(
        "[@raventic/sdk] dropdown chat texts not filled: `chatbotUi` is not the facade's chat UI — " +
          'import { dropdownChat } from "@raventic/sdk/assistant" (not from @raventic/sdk-assistant-v1).',
      );
    }
    return filled;
  }
  const chat = (fillChat as ChatTextsFiller)(chatbot, language);
  return chat === chatbot ? filled : { ...filled, chatbot: chat };
}
